import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ChevronRight, KeyRound } from "lucide-react";

import { Switch } from "../../components/ui/switch";
import { API } from "../../constant/api";
import { useUser } from "../../context/user-context";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import GoogleAuthDialog from "./components/GoogleAuthDialog";
import SettingsSection from "./components/SettingsSection";

const Security = () => {
  const axiosInstance = useAuthAxios();
  const { merchantDetails, refetch2 } = useUser();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [googleAuthOpen, setGoogleAuthOpen] = useState(false);

  const settings = merchantDetails?.user?.account?.settings;
  const emailActive = !!settings?.["2fa"]?.active;
  const googleActive = !!settings?.["2fa-auth-app"]?.active;

  const onToggleError = (e: any) =>
    toast.error(errorMessage(e?.response?.data?.message || e?.message));

  const toggleEmail = useMutation({
    // The endpoint encodes the target state, so send the opposite of current.
    mutationFn: async () => {
      const { data } = await axiosInstance.post(
        API.toggle2Fa(!emailActive),
        {}
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Email 2FA updated");
      refetch2();
    },
    onError: onToggleError,
  });

  const disableGoogle = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post(API.deactivateGoogleAuth, {});
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Authenticator app disabled");
      refetch2();
    },
    onError: onToggleError,
  });

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Password"
        description="Use a strong password you don't reuse anywhere else."
      >
        <button
          type="button"
          onClick={() => setPasswordOpen(true)}
          className="flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-muted"
        >
          <KeyRound className="size-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-semibold text-foreground">
              Change password
            </span>
            <span className="block text-[12px] font-medium text-muted-foreground">
              Requires a code sent to your email
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </SettingsSection>

      <SettingsSection
        title="Two-factor authentication"
        description="Ask for a second code when you sign in or change sensitive settings."
      >
        <div className="divide-y rounded-xl border">
          <div className="flex items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-foreground">Email</p>
              <p className="mt-0.5 text-[12px] font-medium text-muted-foreground">
                Send a one-time code to your email address.
              </p>
            </div>
            <Switch
              checked={emailActive}
              disabled={toggleEmail.isPending}
              onCheckedChange={() => toggleEmail.mutate()}
              aria-label="Email two-factor authentication"
            />
          </div>

          <div className="flex items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-foreground">
                Authenticator app
              </p>
              <p className="mt-0.5 text-[12px] font-medium text-muted-foreground">
                Use Google Authenticator to generate codes.
              </p>
            </div>
            <Switch
              checked={googleActive}
              disabled={disableGoogle.isPending}
              // Turning it on needs QR enrolment; turning it off is a direct call.
              onCheckedChange={() => {
                if (googleActive) {
                  disableGoogle.mutate();
                } else {
                  setGoogleAuthOpen(true);
                }
              }}
              aria-label="Authenticator app two-factor authentication"
            />
          </div>
        </div>
      </SettingsSection>

      <ChangePasswordDialog
        open={passwordOpen}
        onOpenChange={setPasswordOpen}
      />
      <GoogleAuthDialog
        open={googleAuthOpen}
        onOpenChange={setGoogleAuthOpen}
      />
    </div>
  );
};

export default Security;
